import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api/client'
import type { OaMapView, SourceParagraph } from '../api/client'

function Failure({ error }: { error: unknown }) {
  if (!error) return null
  return (
    <p role="alert" className="warn">
      {error instanceof Error
        ? error.message
        : 'The request failed. Your saved work is unchanged.'}
    </p>
  )
}

export function OaMapPanel({ caseId, fileId }: { caseId: string; fileId: string }) {
  const client = useQueryClient()
  const key = ['oa-map', caseId, fileId]
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.getOaMap(caseId, fileId),
    retry: false,
  })
  const [paragraphs, setParagraphs] = useState<SourceParagraph[]>([])
  const [note, setNote] = useState('')
  const [editing, setEditing] = useState(false)
  const [editingMapId, setEditingMapId] = useState<string | null>(null)
  useEffect(() => {
    if (editing) {
      return
    }
    setParagraphs(query.data?.paragraphs ?? [])
    setEditing(false)
    setNote('')
  }, [query.data, fileId])
  const saved = (map: OaMapView | null) => {
    setEditing(false)
    setEditingMapId(null)
    client.setQueryData(key, map)
    void client.invalidateQueries({ queryKey: ['reply-review', caseId] })
  }
  const prepare = useMutation({
    mutationFn: () => api.prepareOaMap(caseId, fileId),
    onSuccess: saved,
  })
  const confirm = useMutation({
    mutationFn: () =>
      api.confirmOaMap(caseId, editingMapId ?? query.data!.id, {
        paragraphs,
        review_note:
          note.trim() || 'Source corrections saved; any remaining uncertainties are retained.',
        preflight: false,
      }),
    onSuccess: saved,
  })
  const busy = prepare.isPending || confirm.isPending
  const change = (index: number, update: Partial<SourceParagraph>) => {
    if (!editing) setEditingMapId(query.data?.id ?? null)
    setParagraphs((rows) => rows.map((row, i) => (i === index ? { ...row, ...update } : row)))
    setEditing(true)
  }
  const addParagraph = (index: number, page: number) => {
    if (!editing) setEditingMapId(query.data?.id ?? null)
    const added: SourceParagraph = {
      id: `new-${crypto.randomUUID()}`,
      label: '',
      text: '',
      first_page: page,
      last_page: page,
      kind: 'paragraph',
      claims: [''],
      uncertainty: '',
    }
    setParagraphs((rows) => [...rows.slice(0, index), added, ...rows.slice(index)])
    setEditing(true)
  }
  return (
    <section aria-label="OA paragraph review" data-testid="oa-map-panel">
      <p className="strong">Review or correct the OA reading</p>
      <p className="small muted">
        Check the main OA against the PDF, including its final paragraphs and reliefs. Annexure
        numbering is separate. Claim summaries describe allegations, not confirmed facts.
      </p>
      <Failure error={query.error || prepare.error || confirm.error} />
      {query.isLoading ? <p>Loading the saved OA reading…</p> : null}
      {!query.isLoading && !query.data ? (
        <button
          type="button"
          className="btn"
          disabled={busy || !!query.error}
          onClick={() => prepare.mutate()}
        >
          {busy ? 'Reading the OA…' : 'Prepare OA paragraph map'}
        </button>
      ) : null}
      {query.data ? (
        <>
          {editingMapId && editingMapId !== query.data.id ? (
            <p role="alert" className="warn">
              Another source revision arrived. Your unsaved corrections remain here; reopen the
              map and reconcile them before confirming.
            </p>
          ) : null}
          <p>
            {query.data.confirmed && !editing
              ? `Source reading reviewed by ${query.data.created_by}.`
              : 'You can draft with this reading. Any uncertainties remain flagged.'}
          </p>
          {query.data.source_url ? (
            <a href={query.data.source_url} target="_blank" rel="noreferrer">
              Open the original OA PDF
            </a>
          ) : null}
          {query.data.extraction_notes ? (
            <details>
              <summary>Notes from reading the PDF</summary>
              <p>{query.data.extraction_notes}</p>
            </details>
          ) : null}
          {query.data.review_note ? <p>Previous review: {query.data.review_note}</p> : null}
          <details>
            <summary>Original extraction (preserved)</summary>
            {query.data.original_paragraphs.map((p) => (
              <p key={p.id}>
                <strong>{p.label}</strong> {p.text}
              </p>
            ))}
          </details>
          <button
            type="button"
            className="quiet"
            disabled={busy}
            onClick={() => addParagraph(0, paragraphs[0]?.first_page ?? 1)}
          >
            Add a missing paragraph at the beginning
          </button>
          {paragraphs.map((p, index) => (
            <details key={p.id}>
              <summary>
                OA {p.label} · PDF {p.first_page}–{p.last_page} ·{' '}
                {p.kind === 'heading' ? 'Heading' : `${p.claims.length} claims`}
                {p.uncertainty ? ' · needs attention' : ''}
              </summary>
              <label className="field">
                Paragraph label
                <input
                  aria-label={`Label for paragraph ${index + 1}`}
                  value={p.label}
                  disabled={busy}
                  onChange={(e) => change(index, { label: e.target.value })}
                />
              </label>
              <label className="field">
                Original paragraph text
                <textarea
                  aria-label={`OA text ${p.label}`}
                  rows={6}
                  value={p.text}
                  disabled={busy}
                  onChange={(e) => change(index, { text: e.target.value })}
                />
              </label>
              <label className="field">
                First PDF page
                <input
                  type="number"
                  min={1}
                  max={query.data?.page_count}
                  value={p.first_page}
                  disabled={busy}
                  onChange={(e) => change(index, { first_page: Number(e.target.value) })}
                />
              </label>
              <label className="field">
                Last PDF page
                <input
                  type="number"
                  min={p.first_page}
                  max={query.data?.page_count}
                  value={p.last_page}
                  disabled={busy}
                  onChange={(e) => change(index, { last_page: Number(e.target.value) })}
                />
              </label>
              <label className="field">
                Kind
                <select
                  aria-label={`Kind for OA ${p.label}`}
                  value={p.kind}
                  disabled={busy}
                  onChange={(e) =>
                    change(index, {
                      kind: e.target.value as SourceParagraph['kind'],
                      claims: e.target.value === 'heading' ? [] : p.claims,
                    })
                  }
                >
                  <option value="paragraph">Paragraph requiring a reply</option>
                  <option value="heading">Structural heading only</option>
                </select>
              </label>
              {p.kind === 'paragraph' ? (
                <label className="field">
                  Material claims — one per line
                  <textarea
                    aria-label={`Claims for OA ${p.label}`}
                    rows={4}
                    value={p.claims.join('\n')}
                    disabled={busy}
                    onChange={(e) => change(index, { claims: e.target.value.split('\n') })}
                  />
                </label>
              ) : null}
              <label className="field">
                Unresolved source-text uncertainty
                <textarea
                  value={p.uncertainty}
                  disabled={busy}
                  onChange={(e) => change(index, { uncertainty: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="quiet"
                disabled={busy}
                onClick={() => {
                  if (!editing) setEditingMapId(query.data?.id ?? null)
                  setParagraphs((rows) => rows.filter((row) => row.id !== p.id))
                  setEditing(true)
                }}
              >
                Exclude this entry from the reviewed map
              </button>
              <button
                type="button"
                className="quiet"
                disabled={busy}
                onClick={() => addParagraph(index + 1, p.last_page)}
              >
                Add a missing paragraph after this one
              </button>
            </details>
          ))}
          <label className="field">
            Source review note (optional)
            <textarea
              aria-label="Source review note"
              value={note}
              disabled={busy}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Record the pages reviewed and explain any corrections or exclusions."
            />
          </label>
          <p>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => confirm.mutate()}
            >
              {confirm.isPending ? 'Saving…' : 'Save OA corrections'}
            </button>
          </p>
          <p className="small muted">
            Saving preserves previous readings and any remaining uncertainties. Source changes
            apply to the next draft; existing replies remain unchanged.
          </p>
        </>
      ) : null}
    </section>
  )
}

/** A small officer-facing correction flow; the full extraction stays internal. */
export function UnclearOaText({ caseId, fileId }: { caseId: string; fileId: string }) {
  const client = useQueryClient()
  const key = ['oa-map', caseId, fileId]
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.getOaMap(caseId, fileId),
    retry: false,
  })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [correction, setCorrection] = useState('')
  const confirm = useMutation({
    mutationFn: ({ paragraph, text }: { paragraph: SourceParagraph; text: string }) => {
      const source = query.data!
      return api.confirmOaMap(caseId, source.id, {
        paragraphs: source.paragraphs.map((row) =>
          row.id === paragraph.id ? { ...row, text: text.trim(), uncertainty: '' } : row,
        ),
        review_note:
          text.trim() === paragraph.text.trim()
            ? `Officer confirmed the text of OA ${paragraph.label}.`
            : `Officer corrected the text of OA ${paragraph.label}.`,
        preflight: false,
      })
    },
    onSuccess: (map) => {
      setEditingId(null)
      setCorrection('')
      client.setQueryData(key, map)
      void client.invalidateQueries({ queryKey: ['reply-review', caseId] })
    },
  })
  const uncertain =
    query.data?.paragraphs.filter((paragraph) => paragraph.uncertainty.trim()) ?? []
  return (
    <section aria-label="Unclear OA text" className="d18-unclear-text">
      <p className="small muted">
        This only checks text the app could not read clearly. It does not block drafting.
      </p>
      {query.data?.source_url ? (
        <a href={query.data.source_url} target="_blank" rel="noreferrer">
          Open the original OA PDF
        </a>
      ) : null}
      {uncertain.map((paragraph) => (
        <article key={paragraph.id} className="d18-question">
          <h3>OA {paragraph.label}</h3>
          <p>{paragraph.text}</p>
          <p className="small muted">Why it was flagged: {paragraph.uncertainty}</p>
          {editingId === paragraph.id ? (
            <label className="field">
              Correct text
              <textarea
                rows={5}
                value={correction}
                onChange={(event) => setCorrection(event.target.value)}
              />
            </label>
          ) : null}
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={confirm.isPending}
              onClick={() => confirm.mutate({ paragraph, text: paragraph.text })}
            >
              Text is correct
            </button>
            {editingId === paragraph.id ? (
              <button
                type="button"
                className="quiet"
                disabled={confirm.isPending || !correction.trim()}
                onClick={() => confirm.mutate({ paragraph, text: correction })}
              >
                Save corrected text
              </button>
            ) : (
              <button
                type="button"
                className="quiet"
                disabled={confirm.isPending}
                onClick={() => {
                  setEditingId(paragraph.id)
                  setCorrection(paragraph.text)
                }}
              >
                Correct text
              </button>
            )}
          </div>
        </article>
      ))}
      {!query.isLoading && !uncertain.length ? <p>No unclear OA text remains.</p> : null}
      <Failure error={query.error || confirm.error} />
    </section>
  )
}
